import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getMerchantAPIUrl } from '../../services/apollo/urlResolver';
import * as Sentry from '@sentry/react';
import type { CurrencyFeatureFlags } from '../utils';

export interface FeatureFlags extends CurrencyFeatureFlags {
  walletConnect: boolean | undefined;
  binancePay: boolean | undefined;
}

interface FeatureFlagContext {
  featureFlags: FeatureFlags;
}

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  walletConnect: undefined,
  binancePay: undefined,
  widgetSolCurrency: undefined,
  widgetBnbCurrency: undefined,
};

const FeatureFlagContext = createContext<FeatureFlagContext>({
  featureFlags: DEFAULT_FEATURE_FLAGS,
});

const snakeToCamel = (s: string): string =>
  s.replace(/(_\w)/g, (splitWord: string): string =>
    splitWord[1].toUpperCase()
  );

const fetchFeatureFlags = async (): Promise<FeatureFlags> => {
  try {
    const response = await fetch(`${getMerchantAPIUrl()}/flags`);

    if (!response.ok) {
      throw new Error(`Failed to fetch feature flags: ${response.status}`);
    }

    const data = await response.json();

    return data.data.reduce(
      (
        acc: FeatureFlags,
        item: { attributes: { name: string; enabled: boolean } }
      ) => ({
        ...acc,
        [snakeToCamel(item.attributes.name)]: item.attributes.enabled,
      }),
      DEFAULT_FEATURE_FLAGS
    );
  } catch (error) {
    Sentry.withScope((scope: Sentry.Scope) => {
      scope.setExtra('errorType', (error as Error)?.name);
      scope.setExtra('errorMessage', (error as Error)?.message);
      scope.setTag('error_type', 'network_error');

      Sentry.captureException(error);
    });

    return DEFAULT_FEATURE_FLAGS;
  }
};

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(
    DEFAULT_FEATURE_FLAGS
  );

  useEffect(() => {
    const loadFeatureFlags = async () => {
      const flags = await fetchFeatureFlags();
      setFeatureFlags(flags);
    };

    loadFeatureFlags();
  }, []);

  return (
    <FeatureFlagContext.Provider value={{ featureFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = () => useContext(FeatureFlagContext);
